<template>
  <span class="loading-indicator" role="status" aria-live="polite">
    <span class="dots" aria-hidden="true">
      <span class="dot"></span>
      <span class="dot"></span>
      <span class="dot"></span>
    </span>
    <transition name="status-fade" mode="out-in">
      <span class="status-text" :key="statusText">{{ statusText }}</span>
    </transition>
  </span>
</template>

<script>
// After this long without a reply, soften the copy to a gentle "still
// working" line so a slow turn never reads as stuck.
const LONG_WAIT_MS = 5000;

export default {
  name: "MessageLoading",
  props: {
    // Which stage of the turn this loader represents. "listening" plays while
    // the student's speech is being transcribed; "thinking" while Arti composes
    // a reply. Anchored to a real signal: the loader flips to "thinking" the
    // moment the student's transcript actually appears in the chat.
    phase: {
      type: String,
      default: "thinking",
      validator: (value) => ["listening", "thinking"].includes(value),
    },
  },
  data() {
    return {
      longWait: false,
      longWaitTimer: null,
    };
  },
  computed: {
    statusText() {
      const base = this.phase === "listening" ? "listening" : "thinking";
      const key = this.longWait ? `chatLoading.${base}Long` : `chatLoading.${base}`;
      return this.$t(key);
    },
  },
  watch: {
    // Restart the long-wait countdown whenever the phase changes so each
    // phase gets its own grace period before softening.
    phase() {
      this.longWait = false;
      this.startLongWaitTimer();
    },
  },
  mounted() {
    this.startLongWaitTimer();
  },
  beforeUnmount() {
    clearTimeout(this.longWaitTimer);
  },
  methods: {
    startLongWaitTimer() {
      clearTimeout(this.longWaitTimer);
      this.longWaitTimer = setTimeout(() => {
        this.longWait = true;
      }, LONG_WAIT_MS);
    },
  },
};
</script>

<style scoped>
.loading-indicator {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.dots {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: var(--al-text-muted);
  /* Soft staggered wave; pure CSS so there's no JS timer for the dots. */
  animation: dot-wave 1.4s ease-in-out infinite both;
}

.dot:nth-child(1) {
  animation-delay: -0.32s;
}
.dot:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes dot-wave {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-3px);
  }
}

.status-text {
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  /* Premium sheen: a slow highlight sweeps across muted slate text. */
  background: linear-gradient(
    90deg,
    var(--al-text-muted) 0%,
    var(--al-text-muted) 35%,
    var(--al-light-blue) 50%,
    var(--al-text-muted) 65%,
    var(--al-text-muted) 100%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  animation: status-shimmer 2.4s linear infinite;
}

@keyframes status-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

/* Smooth crossfade when the copy changes (phase flip or long-wait softening). */
.status-fade-enter-active,
.status-fade-leave-active {
  transition: opacity 0.25s ease;
}
.status-fade-enter-from,
.status-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .dot,
  .status-text {
    animation: none;
  }
  .status-text {
    -webkit-text-fill-color: var(--al-text-muted);
    color: var(--al-text-muted);
  }
}
</style>
